import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';
import {
    calculateWithholding,
    updatePrizePayout,
} from '../../../data/api/prizePayouts/PrizePayoutRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const PAYMENT_METHODS = [
    { value: 'EFECTIVO', label: 'Efectivo' },
    { value: 'TRANSFERENCIA', label: 'Transferencia bancaria' },
    { value: 'NEQUI', label: 'Nequi' },
    { value: 'DAVIPLATA', label: 'Daviplata' },
];

const fmtCOP = (cents) => Number(cents ?? 0) / 100;

const numberFieldProps = (extra = {}) => ({
    type: 'number',
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
        if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
    },
    inputProps: { min: 1, step: 1000, ...extra },
});

const fmtCOPLabel = (cents) =>
    Number(cents ?? 0).toLocaleString('es-CO', {
        style: 'currency', currency: 'COP', maximumFractionDigits: 0,
    }).replace('COP', '').trim();

/**
 * Modal de edición de un PrizePayout.
 *
 * Solo admin lo abre. Permite editar monto, método, plazo, referencia,
 * titular y notas.  Si el pago estaba SIGNED y se cambia un campo
 * "sensible" (todo menos `notes`), al guardar el backend resetea la
 * firma y vuelve a PENDING — se le advierte al admin antes de guardar.
 */
const EditPayoutModal = ({ open, onClose, payout, onUpdated }) => {
    const { token } = useIndex();
    const [submitting, setSubmitting] = useState(false);
    const [breakdown, setBreakdown] = useState(null);
    const [calcLoading, setCalcLoading] = useState(false);

    // Estado local del formulario.
    const [grossCop, setGrossCop] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('EFECTIVO');
    const [paymentReference, setPaymentReference] = useState('');
    const [paymentAccountHolder, setPaymentAccountHolder] = useState('');
    const [deadlineAt, setDeadlineAt] = useState('');
    const [notes, setNotes] = useState('');

    // Snapshot inicial para detectar diff y mostrar warning de firma.
    const initial = useMemo(() => ({
        grossCop: fmtCOP(payout?.gross_amount),
        paymentMethod: payout?.payment_method ?? 'EFECTIVO',
        paymentReference: payout?.payment_reference ?? '',
        paymentAccountHolder: payout?.payment_account_holder ?? '',
        deadlineAt: payout?.deadline_at ?? '',
        notes: payout?.notes ?? '',
    }), [payout]);

    useEffect(() => {
        if (!open || !payout) return;
        setGrossCop(initial.grossCop);
        setPaymentMethod(initial.paymentMethod);
        setPaymentReference(initial.paymentReference);
        setPaymentAccountHolder(initial.paymentAccountHolder);
        setDeadlineAt(initial.deadlineAt);
        setNotes(initial.notes);
        setBreakdown({
            gross: payout?.gross_amount,
            withholding: payout?.withholding_amount,
            net: payout?.net_amount,
            applies: !!payout?.withholding_applies,
            applied_rate: payout?.withholding_rate_snapshot ?? 0,
            uvt_value_cop: payout?.uvt_value_cop_snapshot ?? 0,
            threshold_uvt: payout?.withholding_threshold_uvt_snapshot ?? 0,
            threshold_cents: (payout?.uvt_value_cop_snapshot ?? 0)
                * (payout?.withholding_threshold_uvt_snapshot ?? 0)
                * 100,
        });
    }, [open, payout, initial]);

    const grossCents = (Number(grossCop) || 0) * 100;

    // Recalcula retefuente al cambiar el monto bruto.
    useDebounce(async () => {
        if (!open || grossCents === Number(payout?.gross_amount ?? 0)) return;
        if (!grossCents || grossCents < 100) {
            setBreakdown(null);
            return;
        }
        setCalcLoading(true);
        try {
            const { data } = await calculateWithholding(token, grossCents);
            if (data?.success) setBreakdown(data.data);
        } catch {
            // Silencioso: el preview es accesorio.
        } finally {
            setCalcLoading(false);
        }
    }, 350, [grossCents, open]);

    /**
     * Detecta si los cambios actuales tocan campos "sensibles a la
     * firma". Si sí + estado SIGNED, advertimos al admin que la firma
     * será invalidada.
     */
    const sensitiveChanges = useMemo(() => {
        if (!payout) return false;
        return (
            grossCents !== Number(payout.gross_amount) ||
            paymentMethod !== initial.paymentMethod ||
            (paymentReference || '') !== (initial.paymentReference || '') ||
            (paymentAccountHolder || '') !== (initial.paymentAccountHolder || '') ||
            (deadlineAt || '') !== (initial.deadlineAt || '')
        );
    }, [payout, grossCents, paymentMethod, paymentReference, paymentAccountHolder, deadlineAt, initial]);

    const willResetSignature = !!payout && payout.status === 'SIGNED' && sensitiveChanges;

    const hasChanges = useMemo(() => {
        if (!payout) return false;
        return (
            grossCents !== Number(payout.gross_amount) ||
            paymentMethod !== initial.paymentMethod ||
            (paymentReference || '') !== (initial.paymentReference || '') ||
            (paymentAccountHolder || '') !== (initial.paymentAccountHolder || '') ||
            (deadlineAt || '') !== (initial.deadlineAt || '') ||
            (notes || '') !== (initial.notes || '')
        );
    }, [payout, grossCents, paymentMethod, paymentReference, paymentAccountHolder, deadlineAt, notes, initial]);

    const canSubmit = !submitting && payout && hasChanges && grossCents >= 100 && !!paymentMethod && !!deadlineAt;

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;

        // Construimos el body solo con los campos que cambiaron, para
        // que el backend pueda decidir bien si resetear la firma.
        const body = {};
        if (grossCents !== Number(payout.gross_amount)) body.gross_amount = grossCents;
        if (paymentMethod !== initial.paymentMethod) body.payment_method = paymentMethod;
        if ((paymentReference || '') !== (initial.paymentReference || '')) body.payment_reference = paymentReference || null;
        if ((paymentAccountHolder || '') !== (initial.paymentAccountHolder || '')) body.payment_account_holder = paymentAccountHolder || null;
        if ((deadlineAt || '') !== (initial.deadlineAt || '')) body.deadline_at = deadlineAt;
        if ((notes || '') !== (initial.notes || '')) body.notes = notes || null;

        setSubmitting(true);
        try {
            const { data } = await updatePrizePayout(token, payout.uuid, body);
            if (data?.success) {
                Report.success('Pago actualizado', data?.message ?? 'Cambios guardados.', 'Aceptar');
                onUpdated?.(data?.data);
                onClose?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => !submitting && onClose?.()}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto' }}>
                    <CardContent>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <EditOutlinedIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6">Editar pago</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Cambios sustantivos invalidan la firma del ganador.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => !submitting && onClose?.()} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        {payout?.winner && (
                            <WinnerBox>
                                <Typography variant="overline" color="text.secondary">Ganador</Typography>
                                <Typography sx={{ fontWeight: 900, color: '#10285f' }}>
                                    {payout.winner.user_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Chip size="small" label={`Doc: ${payout.winner.document ?? '-'}`} />
                                    <Chip size="small" label={`Estado: ${payout.status}`} />
                                </Box>
                            </WinnerBox>
                        )}

                        {willResetSignature && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Este pago ya fue firmado. Al guardar los cambios, la firma será
                                invalidada y el ganador deberá firmar nuevamente con los términos actualizados.
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <form onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Valor bruto (COP)"
                                        helperText="Mínimo $5.000 COP"
                                        {...numberFieldProps({ min: 5000, step: 1000 })}
                                        value={grossCop}
                                        onChange={(e) => setGrossCop(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Método de pago</InputLabel>
                                        <Select
                                            label="Método de pago"
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        >
                                            {PAYMENT_METHODS.map((m) => (
                                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Fecha límite de entrega"
                                        InputLabelProps={{ shrink: true }}
                                        value={deadlineAt ?? ''}
                                        onChange={(e) => setDeadlineAt(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Titular cuenta destino"
                                        value={paymentAccountHolder}
                                        onChange={(e) => setPaymentAccountHolder(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Referencia de pago"
                                        value={paymentReference}
                                        onChange={(e) => setPaymentReference(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        label="Notas (no resetea la firma)"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <BreakdownBox $applies={!!breakdown?.applies}>
                                <Typography variant="overline" color="text.secondary">
                                    Desglose financiero {calcLoading && '(calculando...)'}
                                </Typography>
                                <Row>
                                    <span>Bruto</span>
                                    <strong>${fmtCOPLabel(breakdown?.gross ?? grossCents)}</strong>
                                </Row>
                                <Row>
                                    <span>
                                        Retefuente {breakdown?.applies
                                            ? `(${Math.round((breakdown.applied_rate ?? 0) * 100)}% sobre el premio)`
                                            : '(no aplica)'}
                                    </span>
                                    <strong>− ${fmtCOPLabel(breakdown?.withholding ?? 0)}</strong>
                                </Row>
                                <Row className="total">
                                    <span>Neto a entregar</span>
                                    <strong>${fmtCOPLabel(breakdown?.net ?? grossCents)}</strong>
                                </Row>
                            </BreakdownBox>

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                disabled={!canSubmit}
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>{willResetSignature ? 'Guardar y reiniciar firma' : 'Guardar cambios'}</span>
                            </LoadingButton>
                        </form>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default EditPayoutModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 640px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.5rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
`;

const WinnerBox = styled.div`
    padding: 0.7rem 0.85rem;
    border-radius: 12px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const BreakdownBox = styled.div`
    margin-top: 1.2rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: ${({ $applies }) =>
        $applies ? 'rgba(211, 47, 47, 0.06)' : 'rgba(46, 125, 50, 0.06)'};
    border: 1px solid ${({ $applies }) =>
        $applies ? 'rgba(211, 47, 47, 0.22)' : 'rgba(46, 125, 50, 0.22)'};
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0.2rem 0;
    color: rgba(15, 34, 77, 0.78);

    &.total {
        margin-top: 0.4rem;
        padding-top: 0.5rem;
        border-top: 1px dashed rgba(15, 34, 77, 0.12);
        color: #10285f;
        font-size: 1.05rem;
    }
`;
